import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar,
  TrendingUp,
  DollarSign,
  ArrowRight,
  Download
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const YEARS = ["2024", "2025", "2026", "2027"];

const NOW = new Date();
const CURRENT_MONTH_NAME = MONTHS[NOW.getMonth()];
const CURRENT_YEAR_STR = String(NOW.getFullYear());

const MonthlyOrders = () => {
  const [selectedMonth, setSelectedMonth] = useState(CURRENT_MONTH_NAME);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR_STR);
  const [financials, setFinancials] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const currentMonth = `${selectedMonth} ${selectedYear}`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [financialsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/analytics/monthly/${encodeURIComponent(currentMonth)}`),
        axios.get(`${API}/orders?month=${encodeURIComponent(currentMonth)}`)
      ]);
      setFinancials(financialsRes.data);
      setAllOrders(ordersRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportSummary = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/export/monthly/${encodeURIComponent(currentMonth)}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `monthly_summary_${currentMonth.replace(' ', '_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export downloaded");
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleExportOrders = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/export/orders?month=${encodeURIComponent(currentMonth)}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orders_${currentMonth.replace(' ', '_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export downloaded");
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Calculate totals
  const totals = financials.reduce((acc, client) => ({
    revenue: acc.revenue + (client.revenue || 0),
    realised: acc.realised + (client.realised || 0),
    completed_orders: acc.completed_orders + (client.completed_orders || 0),
    total_orders: acc.total_orders + (client.total_orders || 0)
  }), { revenue: 0, realised: 0, completed_orders: 0, total_orders: 0 });

  totals.pending = totals.revenue - totals.realised;
  totals.percentage_rr = totals.revenue > 0 
    ? ((totals.realised / totals.revenue) * 100).toFixed(2)
    : 0;

  // Chart data
  const chartData = financials.map(f => ({
    name: f.client_name.length > 10 ? f.client_name.slice(0, 10) + "..." : f.client_name,
    generated: f.revenue || 0,
    realised: f.realised || 0
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="monthly-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Monthly Overview
          </h1>
          <p className="text-gray-500 mt-1">
            Consolidated financial view across all clients
          </p>
        </div>
      </div>

      {/* Month Selector */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-gray-400" />
          <span className="text-sm text-gray-500">Select Period:</span>
        </div>
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-36 bg-white" data-testid="month-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((month) => (
              <SelectItem key={month} value={month}>
                {month}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-24 bg-white" data-testid="year-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {YEARS.map((year) => (
              <SelectItem key={year} value={year}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={handleExportSummary}
            disabled={exporting}
            className="btn-secondary"
            data-testid="export-summary-btn"
          >
            <Download size={16} /> Summary
          </button>
          <button
            onClick={handleExportOrders}
            disabled={exporting}
            className="btn-secondary"
            data-testid="export-orders-btn"
          >
            <Download size={16} /> Orders
          </button>
        </div>
      </div>

      {/* Total Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="monthly-stats">
        <div className="stat-card border-l-4 border-l-blue-500">
          <p className="stat-card-title">Total Revenue</p>
          <p className="stat-card-value primary">
            {formatCurrency(totals.revenue)}
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-card-title">Total Realised</p>
          <p className="stat-card-value success">
            {formatCurrency(totals.realised)}
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-card-title">Pending</p>
          <p className="stat-card-value warning">
            {formatCurrency(totals.pending)}
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-card-title">% Realised</p>
          <p className="stat-card-value" style={{ color: "#8B5CF6" }}>
            {totals.percentage_rr}%
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-card-title">Orders</p>
          <p className="stat-card-value">
            {totals.completed_orders}/{totals.total_orders}
          </p>
          <p className="stat-card-subtitle">Completed/Total</p>
        </div>
      </div>

      {/* Chart and Client Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart */}
        <div className="stat-card" data-testid="monthly-chart">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Client Revenue Breakdown
          </h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} layout="vertical">
                <XAxis 
                  type="number" 
                  stroke="#94A3B8" 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `₹${value / 1000}k`}
                />
                <YAxis 
                  type="category"
                  dataKey="name" 
                  stroke="#94A3B8" 
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  width={80}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    color: '#1E293B'
                  }}
                  formatter={(value) => [formatCurrency(value), '']}
                />
                <Bar dataKey="generated" name="Generated" fill="#DBEAFE" radius={[0, 4, 4, 0]} />
                <Bar dataKey="realised" name="Realised" fill="#2563EB" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No data for this month
            </div>
          )}
        </div>

        {/* Client List */}
        <div className="stat-card" data-testid="client-financials">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Client Financials
          </h2>
          {financials.length > 0 ? (
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {financials.map((client) => (
                <Link
                  key={client.client_id}
                  to={`/clients/${client.client_id}`}
                  className="block p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group"
                  data-testid={`client-financial-${client.client_id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">{client.client_name}</h3>
                    <ArrowRight size={16} className="text-gray-400 group-hover:text-gray-600 transition-colors" />
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div>
                      <p className="text-gray-500">Revenue</p>
                      <p className="text-gray-900 font-mono">{formatCurrency(client.revenue || 0)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Realised</p>
                      <p className="text-green-600 font-mono">{formatCurrency(client.realised || 0)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Pending</p>
                      <p className="text-amber-600 font-mono">{formatCurrency(client.pending || 0)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">%RR</p>
                      <p className="text-blue-600 font-mono">{client.percentage_rr || 0}%</p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full transition-all"
                        style={{ width: `${Math.min(client.percentage_rr || 0, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">
                      {client.completed_orders || 0}/{client.total_orders || 0} orders
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No clients with orders for this month
            </div>
          )}
        </div>
      </div>

      {/* All Orders Table */}
      <div className="stat-card p-0" data-testid="all-orders-table">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            All Orders - {currentMonth}
          </h2>
          <span className="text-sm text-gray-500">{allOrders.length} orders</span>
        </div>
        
        {allOrders.length > 0 ? (
          <div className="data-table-wrapper border-0 rounded-none">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Exam</th>
                  <th>Subject</th>
                  <th>Particulars</th>
                  <th>Quantity</th>
                  <th>Status</th>
                  <th>Rate (₹)</th>
                </tr>
              </thead>
              <tbody>
                {allOrders.map((order) => {
                  const client = financials.find(f => f.client_id === order.client_id);
                  return (
                    <tr key={order.id}>
                      <td className="font-medium">
                        {client?.client_name || "Unknown"}
                      </td>
                      <td>{order.exam}</td>
                      <td>{order.subject}</td>
                      <td>{order.particulars}</td>
                      <td className="font-mono">{order.quantity || "-"}</td>
                      <td>
                        <span className={`badge ${
                          order.status === "Completed" ? "badge-success" :
                          order.status === "In Progress" ? "badge-info" : "badge-warning"
                        }`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="font-mono text-right">{formatCurrency(order.rate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No orders for {currentMonth}
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyOrders;
